import type { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Analytics } from '@dub/analytics/react';

import { AuthProvider } from '@/features/auth/ui/auth-provider';

import { ENV } from '@/shared/config/env';
import { WebSocketProvider } from '@/shared/providers/websocket-provider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 30 * 1000, // 30 seconds
    },
  },
});

// Make queryClient available globally for logout
if (typeof window !== 'undefined') {
  (window as unknown as { __REACT_QUERY_CLIENT__: QueryClient }).__REACT_QUERY_CLIENT__ =
    queryClient;
}

interface ProvidersProps {
  children: ReactNode;
}

export const Providers = ({ children }: ProvidersProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Analytics publishableKey={ENV.DUB_PUBLISHABLE_KEY} />
          <AuthProvider>
            <WebSocketProvider>
              {/* No hardcoded theme class here — <html> owns `dark`/`light`
                * (shared/lib/theme.ts); a stray `dark` on this wrapper would
                * re-enable every `dark:` variant in light mode. */}
              <div className="bg-background text-foreground min-h-screen">{children}</div>
              <Toaster
                position="bottom-right"
                theme="dark"
                toastOptions={{
                  style: {
                    background: 'hsl(var(--card))',
                    color: 'hsl(var(--foreground))',
                    border: '1px solid hsl(var(--border))',
                  },
                }}
              />
            </WebSocketProvider>
          </AuthProvider>
      </HelmetProvider>
      {/* DEV-only: import.meta.env.DEV is statically false in production
        * builds, so rollup drops both the element and the import. */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};
